import React from 'react';
import { Chip } from '@mui/material';

/**
 * Color palette from CANprospect status colors
 * These hex codes correspond to the prospect status background colors
 */
const STATUS_COLORS = {
  // Light cyan/blue (#d7f7fc) - Initial/queued states
  BLUE: '#d7f7fc',
  // Light green (#d7fce4) - Positive/active states
  GREEN: '#d7fce4',
  // Light yellow-green (#f3fcd7) - Good progress states
  YELLOW_GREEN: '#f3fcd7',
  // Light yellow (#fcf6d7) - Positive action states
  YELLOW: '#fcf6d7',
  // Light orange/peach (#fce6d7) - Warning/attention states
  ORANGE: '#fce6d7',
  // Light pink/red (#fcd7d7) - Negative/failure states
  PINK: '#fcd7d7',
  // Light pink/magenta (#fcd7f1) - Unused (reserved for future use)
  MAGENTA: '#fcd7f1',
  // Light purple/lavender (#e6d7fc) - Unused (reserved for future use)
  PURPLE: '#e6d7fc',
};

/**
 * Status color mapping for individual email statuses
 * Individual emails: Processing, Queued, Sent, Delivered, Opened, Clicked, Bounced, Failed
 */
const individualEmailStatusConfig = {
  // Processing - Email is being processed/queued (default initial status)
  // Color: Light cyan/blue (#d7f7fc) - Initial state
  Processing: { backgroundColor: STATUS_COLORS.BLUE, label: 'Processing' },
  
  // Queued - Email is queued (from EmailStatus enum, used in practice)
  // Color: Light cyan/blue (#d7f7fc) - Initial state
  Queued: { backgroundColor: STATUS_COLORS.BLUE, label: 'Queued' },
  
  // Sent - Email has been sent (has PostalMessageID but no delivery events yet)
  // Color: Light green (#d7fce4) - Positive/active state
  Sent: { backgroundColor: STATUS_COLORS.GREEN, label: 'Sent' },
  
  // Delivered - Email was successfully delivered (MessageSent event received)
  // Color: Light yellow-green (#f3fcd7) - Good progress state
  Delivered: { backgroundColor: STATUS_COLORS.YELLOW_GREEN, label: 'Delivered' },
  
  // Opened - Email was opened by recipient (MessageLoaded event received)
  // Color: Light yellow (#fcf6d7) - Positive action state
  Opened: { backgroundColor: STATUS_COLORS.YELLOW, label: 'Opened' },
  
  // Clicked - Recipient clicked a link in the email (MessageLinkClicked event received)
  // Color: Light orange/peach (#fce6d7) - Engagement/attention state
  Clicked: { backgroundColor: STATUS_COLORS.ORANGE, label: 'Clicked' },
  
  // Bounced - Email bounced (MessageBounced or MessageDeliveryFailed with SoftFail)
  // Color: Light orange/peach (#fce6d7) - Warning state (soft failure)
  Bounced: { backgroundColor: STATUS_COLORS.ORANGE, label: 'Bounced' },
  
  // Failed - Email delivery failed permanently (MessageDeliveryFailed with HardFail)
  // Color: Light pink/red (#fcd7d7) - Hard failure/negative state
  Failed: { backgroundColor: STATUS_COLORS.PINK, label: 'Failed' },
};

/**
 * Status color mapping for bulk email statuses
 * Bulk emails: Queued, Scheduled, Processing, Sending, Completed, Cancelled
 */
const bulkEmailStatusConfig = {
  // Queued - Initial status when bulk email is queued for sending
  // Color: Light cyan/blue (#d7f7fc) - Initial state
  Queued: { backgroundColor: STATUS_COLORS.BLUE, label: 'Queued' },
  
  // Scheduled - Email is scheduled for future delivery
  // Color: Light orange/peach (#fce6d7) - Warning/future state
  Scheduled: { backgroundColor: STATUS_COLORS.ORANGE, label: 'Scheduled' },
  
  // Processing - Email is being processed/prepared for sending
  // Color: Light cyan/blue (#d7f7fc) - Initial/processing state
  Processing: { backgroundColor: STATUS_COLORS.BLUE, label: 'Processing' },
  
  // Sending - Email sending has started (first email sent)
  // Color: Light yellow-green (#f3fcd7) - Active sending state
  Sending: { backgroundColor: STATUS_COLORS.YELLOW_GREEN, label: 'Sending' },
  
  // Completed - All individual emails in the bulk send have reached a final status
  // Color: Light green (#d7fce4) - Positive/completed state
  Completed: { backgroundColor: STATUS_COLORS.GREEN, label: 'Completed' },
  
  // Cancelled - Bulk email was cancelled (from CampaignStatus enum)
  // Color: Light pink/red (#fcd7d7) - Negative/failure state
  Cancelled: { backgroundColor: STATUS_COLORS.PINK, label: 'Cancelled' },
};

/**
 * Status color mapping for scheduled email statuses
 * Scheduled emails: Pending, Processing, Failed
 */
const scheduledStatusConfig = {
  // Pending - Email is scheduled and waiting to be sent
  // Color: Light orange/peach (#fce6d7) - Waiting/warning state
  Pending: { backgroundColor: STATUS_COLORS.ORANGE, label: 'Pending' },
  
  // Processing - Scheduled email is being processed
  // Color: Light cyan/blue (#d7f7fc) - Processing state
  Processing: { backgroundColor: STATUS_COLORS.BLUE, label: 'Processing' },
  
  // Failed - Scheduled email failed
  // Color: Light pink/red (#fcd7d7) - Negative/failure state
  Failed: { backgroundColor: STATUS_COLORS.PINK, label: 'Failed' },
};

/**
 * @component StatusChip
 * @description Color-coded status indicator for email status using custom background colors
 * @param {Object} props
 * @param {string} props.status - Email status
 * @param {string} [props.type='email'] - Type of status ('email' | 'bulk' | 'scheduled')
 *   - 'email': Individual email statuses (Processing, Queued, Sent, Delivered, Opened, Clicked, Bounced, Failed)
 *   - 'bulk': Bulk email statuses (Queued, Scheduled, Processing, Sending, Completed, Cancelled)
 *   - 'scheduled': Scheduled email statuses (Pending, Processing, Failed)
 * @returns {JSX.Element}
 */
export function StatusChip({ status, type = 'email' }) {
  // Select the appropriate status config based on type
  let statusConfig;
  if (type === 'scheduled') {
    statusConfig = scheduledStatusConfig;
  } else if (type === 'bulk') {
    statusConfig = bulkEmailStatusConfig;
  } else {
    // Default to individual email statuses
    statusConfig = individualEmailStatusConfig;
  }

  // Get config for the status, or use default for unknown statuses
  const config = statusConfig[status] || { 
    backgroundColor: 'inherit', 
    label: status || 'Unknown' 
  };

  return (
    <Chip
      label={config.label}
      size="small"
      variant="outlined"
      sx={{
        backgroundColor: config.backgroundColor,
        borderColor: config.backgroundColor === 'inherit' ? undefined : config.backgroundColor,
      }}
    />
  );
}

